// AuthContext.js
import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authToken, setAuthToken] = useState(null);
  const [userData, setUserData] = useState(null);
  const [isInitializing, setIsInitializing] = useState(true);

  // Initialize and verify token from localStorage on app start
  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken = localStorage.getItem("auth_token");
      if (storedToken) {
        try {
          // Verify token is still valid
          const res = await axios.get("/api/user/profile", {
            headers: { Authorization: `Bearer ${storedToken}` },
          });
          
          // Token is valid, set authentication state
          setAuthToken(storedToken);
          setUserData(res.data);
          setIsAuthenticated(true);
        } catch (error) {
          // Token is invalid, remove it
          localStorage.removeItem("auth_token");
          setAuthToken(null);
          setUserData(null);
          setIsAuthenticated(false);
        }
      }
      setIsInitializing(false);
    };

    initializeAuth();
  }, []);

  // Logout function
  const logout = () => {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("remember_me");
    setAuthToken(null);
    setUserData(null);
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      setIsAuthenticated, 
      authToken, 
      setAuthToken,
      userData,
      setUserData,
      logout,
      isInitializing
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Helper function to get auth token from either localStorage or context
export function getAuthToken() {
  // First try localStorage (for remembered sessions)
  const storedToken = localStorage.getItem("auth_token");
  if (storedToken) {
    return storedToken;
  }
  
  // If not in localStorage, we need to access context
  // This should be used within components that have access to useAuth
  return null;
}
